import { Component, OnDestroy, OnInit } from '@angular/core';
import { BaseWorkflowModel, WorkflowEndcustomerComponent } from '../workflow-endcustomer.component';
import { ActivatedRoute, Router } from '@angular/router';
import { Model } from 'src/app/app.model';
import { BuyService } from 'src/app/buy/buy.service';
import { SubscriptionService } from 'src/app/subscription/subscription.service';
import { I18NextService } from 'angular-i18next';
import { CompanyService } from 'src/app/company/company.service';
import { WorkflowEndcustomerService } from '../workflow-endcustomer.service';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { runInAction } from 'mobx';
import { Company } from 'src/app/company/company.model';
import { PaymentDetails, SaleOrder } from 'src/app/buy/buy.model';
import { AiTariffService } from 'src/app/ai-tariff.service';
import { AITariff, AITariffType, ChangeAITariffOrder, CreateOrderRequest, CreateOrderRequest_EndcustomerInformation } from '@proto/odoo/order';
import { ToastService } from 'src/app/toast.service';

type WorkflowModel = BaseWorkflowModel & {
  tariffList: AITariff[],
  currentPlan: AITariff,
  selectedPlan: AITariff,
  deliveryDate: string
}

@Component({
  selector: 'app-workflow-endcustomer-ai-tariff',
  templateUrl: './workflow-endcustomer-ai-tariff.component.html',
  standalone: false
})
export class WorkflowEndcustomerAiTariffComponent extends WorkflowEndcustomerComponent<WorkflowModel> implements OnInit, OnDestroy {

  get workflowName(): string {
    return 'change-ai-tariff';
  }

  constructor(
    protected router: Router,
    protected m: Model,
    protected route: ActivatedRoute,
    protected buyService: BuyService,
    protected subscriptionService: SubscriptionService,
    protected toastService: ToastService,
    protected i18next: I18NextService,
    protected companyService: CompanyService,
    protected endcustomerService: WorkflowEndcustomerService,
    protected aiTariffService: AiTariffService,
    protected http: HttpClient
  ) {
    super(route, router, subscriptionService, buyService, m, http, endcustomerService, companyService);
  }

  protected readonly AITariffType = AITariffType;

  async ngOnInit() {
    await super.ngOnInit();

    if (!this.checkSubscriptionValid) {
      this.resetWorkflow();
      return;
    }

    try {
      const result = await firstValueFrom(this.aiTariffService.getAiTariffs());
    
      runInAction(() => {
        this.model.tariffList = result.aiTariffs;
        this.model.currentPlan = this.aiTariffService.getSpecificAITariff(this.model.tariffList, this.model.subscription?.ai_tariff.name ?? '');
      });

      this.navigate(['choose-ai-tariff']);
      this.initialized = true;
    } catch (error) {
      this.toastService.error(this.i18next.t('workflow-endcustomer-ai-tariff_load_resource_error'), 'error');
      this.router.navigate(['/subscriptions', this.model.subscription?.appliance_id]);
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  // step 1: select tariff
  onAITariffSelected(selectedAITariff: string) {
    const tariffType = AITariffType[selectedAITariff as keyof typeof AITariffType];
    const selectedPlan = this.model.tariffList.find(tariff => tariff.tariffType === tariffType);
    if (!selectedPlan) return;
    this.model.selectedPlan = selectedPlan;
    this.navigate(['delivery-date']);
  }

  // step 2: select delivery-date
  public onTariffDeliveryDateBack() {
    this.navigate(['choose-ai-tariff']);
  }

  public onTariffDeliveryDateSelected(deliveryDate: string) {
    this.model.deliveryDate = deliveryDate;
    this.navigate(['invoice-address']);
  }

  // step 3: fill in invoice address
  public onInvoiceAddressBack() {
    this.navigate(['delivery-date']);
  }

  public onInvoiceAddressFilled(company: Company) {
    runInAction(() => {
      this.model.company = company;
    });
    this.navigate(['payment']);
  }

  //step 4: payment
  public onPaymentMethodBack() {
    this.navigate(['invoice-address']);
  }

  public async onPaymentMethodSelected(payment: PaymentDetails) {
    await this.setPaymentDetails(payment);

    const [year, month, day] = this.model.deliveryDate.split('-').map(Number);
    const aiTariffOrder = ChangeAITariffOrder.create({
      tariffType: this.model.selectedPlan.tariffType,
      deliveryDate: { day, month, year }
    });
    const endcustomerInfo = CreateOrderRequest_EndcustomerInformation.create({
      contactId: +this.m.account.currentUser.id
    });
    const request = CreateOrderRequest.create({
      order: {
        $case: "changeAiTariffOrder",
        changeAiTariffOrder: aiTariffOrder
      },
      endcustomerInfo: endcustomerInfo
    });

    let saleOrder;
    if (this.model.subscription) {
      try {
        saleOrder = await firstValueFrom(this.aiTariffService.changeOwnAITariffOrder(request, this.model.subscription.appliance_id));
      } catch (error) {
        this.toastService.error(this.i18next.t('workflow-endcustomer-ai-tariff_change_tariff_request_error'), 'error');
        this.router.navigate(['/subscriptions', this.model.subscription.appliance_id]);
        saleOrder = SaleOrder.fromJson({});
      }
    } else {
      saleOrder = SaleOrder.fromJson({});
    }

    runInAction(() => {
      this.model.saleOrder = saleOrder;
    })
    this.navigate(['checkout-summary']);
  }

  // step 5: summary
  public onSummaryBack() {
    this.m.buy.resetCurrentOrder();
    this.resetWorkflow();
  }

  public onSummaryBuy() {
    this.buyService.confirmOffer({
      name: this.model.subscription!.name,
    }).subscribe({
      next: _ => {
        this.navigate(['thank-you']);
        this.m.buy.resetCurrentOrder();
      },
      error: _ => {
        this.toastService.error(this.i18next.t('workflow-endcustomer-ai-tariff_confirm_offer_error'), 'error');
      }
    });
  }

}
